import Papa from "papaparse";
import { formatInTimeZone } from "date-fns-tz";
import { Ticket } from "@/app/types/tickets";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { TicketStatus } from "../services/LiveQueueService";
import { TagListItem, TagTree } from "@/app/types/tags";

export const daysOrder = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export const displayDate = (value: any) => {
  if (!value) return "";
  const utcDate = new Date(value);
  const pkOffset = 5 * 60;
  const localTime = new Date(utcDate.getTime() + pkOffset * 60 * 1000);
  return formatInTimeZone(localTime, "Asia/Karachi", "dd-MMM-yyyy");
};

export const formatTimeString = (timeString: string): string => {
  // Split the time string and take the first two parts (HH:mm)
  return timeString.split(":").slice(0, 2).join(":");
};

export const displayDateTime = (value: any) => {
  if (!value) return "";
  const utcDate = new Date(value);
  const pkOffset = 5 * 60;
  const localTime = new Date(utcDate.getTime() + pkOffset * 60 * 1000);
  return formatInTimeZone(localTime, "Asia/Karachi", "dd-MMM-yyyy hh:mma");
};

export const displayAddress = (
  address_1?: string | null,
  address_2?: string | null,
  zipCode?: string | null,
  country?: string | null
): string => {
  const parts = [address_1, address_2, zipCode, country];

  // Filter out null, undefined, or empty strings
  const filteredParts = parts.filter((part) => part && part.trim());

  // If no valid parts, return "N/A"
  return filteredParts.length > 0 ? filteredParts.join(", ") : "";
};

export const downloadCSV = <T>(
  data: T[],
  fileName: string,
  mapper: (item: T) => object
) => {
  const filteredData = data.map(mapper);

  const csv = Papa.unparse(filteredData);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function replaceUnderscoreWithSpace(inputString: string) {
  return inputString.replace(/_/g, " "); // Replace all underscores with spaces
}

export const generateTagCodeFromName = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .replace(/_+/g, "_");
};

export const capitalizeFirstLetter = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

export const displayValue = (value: string | number | undefined | null) => {
  return value === null ||
    value == undefined ||
    (typeof value == "string" && value.trim() == "")
    ? ""
    : value;
};

// Function to extract all links
export function extractLinks(data: any[]) {
  let links: string[] = [];

  data.forEach((item: any) => {
    if (item.link && item.link != "/") {
      links.push(item.link); // Add current link
    }

    // Recursively extract links from children if any (skip scoped items — their children are dynamic)
    if (item.children && item.children.length > 0 && !item.is_scoped) {
      links = links.concat(extractLinks(item.children));
    }
  });

  return links;
}

export const saveToLocalStorage = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const formatDate = (date: Date | string | null): string | null => {
  if (!date) return null; // Handle null or undefined dates

  const d = new Date(date);

  // Check if the date is valid
  if (isNaN(d.getTime())) {
    console.error("Invalid date:", date);
    return null; // Return null for invalid dates
  }

  const year: number = d.getFullYear();
  const month: string = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day: string = String(d.getDate()).padStart(2, "0");

  return `${day}-${month}-${year}`;
};

export const formatDateRange = (date: Date | string | null): string | null => {
  if (!date) return null; // Handle null or undefined dates

  const d = new Date(date);

  // Check if the date is valid
  if (isNaN(d.getTime())) {
    console.error("Invalid date:", date);
    return null; // Return null for invalid dates
  }

  const year: number = d.getFullYear();
  const month: string = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day: string = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatDateTime = (value?: string | null): string => {
  if (!value) return "-";

  // 1) Ensure data is interpreted as UTC.
  const dateString = (value.endsWith("Z") || value.includes("+"))
    ? value
    : `${value.replace(" ", "T")}Z`;

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";

  // 2) Convert date to person's timezone explicitly
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // 3) Use date-fns-tz to format directly into their localized timezone
  console.log("got date and format date", value, formatInTimeZone(date, userTimeZone, "MMM d, yyyy h:mm a"))
  return formatInTimeZone(date, userTimeZone, "MMM d, yyyy h:mm a");
};


export const formatNIC = (value?: string) => {
  // Return null if the value is empty, null, or undefined
  if (!value) {
    return null;
  }

  const numericValue = value.replace(/\D/g, "");

  // Add dashes at positions 5 and 12 if length allows
  if (numericValue.length <= 5) {
    return numericValue;
  } else if (numericValue.length <= 12) {
    return `${numericValue.slice(0, 5)}-${numericValue.slice(5)}`;
  } else {
    return `${numericValue.slice(0, 5)}-${numericValue.slice(5, 12)}-${numericValue.slice(12, 13)}`;
  }
};
export const roundToTwoDecimals = (value: number) =>
  Math.floor(value * 100) / 100;

export const inValidToken = (token: string | undefined) => {
  return token !== undefined && token.trim().length > 0;
};

export const formatToPKR = (amount: number | null | undefined) => {
  if (!amount) {
    return "";
  }

  return new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 2,
  })
    .format(amount)
    .replace("PKR", "Rs. ");
};

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

type SearchCriteria = Record<
  string,
  string | number | (string | number)[] | null | undefined
>;
export const generateQueryString = (criteria: SearchCriteria): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(criteria)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((val) => params.append(key, val.toString()));
      } else {
        params.append(key, value.toString());
      }
    }
  }
  return params.toString();
};

export const getLinks = () => {
  try {
    const sidepanel = localStorage.getItem("sidePanel");
    if (!sidepanel) return null;
    const decodedSidepanel = JSON.parse(atob(sidepanel));
    return extractLinks(decodedSidepanel);
  } catch (error) {
    console.error(
      "Error retrieving or parsing sidePanel from localStorage",
      error
    );
    return null;
  }
};

export function deepCompareObjects(
  obj1: Record<string, any>,
  obj2: Record<string, any>
) {
  const differences: { key: string; reason: string }[] = [];

  // Helper function to check if a value is an object
  const isObject = (value: any) =>
    value && typeof value === "object" && !Array.isArray(value);

  // Recursive comparison function
  function compare(
    obj1: Record<string, any>,
    obj2: Record<string, any>,
    path = ""
  ) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Find missing keys in either object
    for (const key of keys1) {
      if (!(key in obj2)) {
        differences.push({
          key: `${path}${key}`,
          reason: "Key is missing in second object",
        });
      }
    }

    for (const key of keys2) {
      if (!(key in obj1)) {
        differences.push({
          key: `${path}${key}`,
          reason: "Key is missing in first object",
        });
      }
    }

    // Compare values for common keys
    for (const key of keys1) {
      if (key in obj2) {
        const value1 = obj1[key];
        const value2 = obj2[key];
        const currentPath = path ? `${path}.${key}` : key;

        if (Array.isArray(value1) && Array.isArray(value2)) {
          // Compare arrays
          if (value1.length !== value2.length) {
            differences.push({
              key: currentPath,
              reason: "Arrays have different lengths",
            });
          } else {
            for (let i = 0; i < value1.length; i++) {
              if (isObject(value1[i]) && isObject(value2[i])) {
                compare(value1[i], value2[i], `${currentPath}[${i}]`);
              } else if (value1[i] !== value2[i]) {
                differences.push({
                  key: `${currentPath}[${i}]`,
                  reason: `Array values differ: ${value1[i]} vs ${value2[i]}`,
                });
              }
            }
          }
        } else if (isObject(value1) && isObject(value2)) {
          // Recursively compare objects
          compare(value1, value2, currentPath);
        } else if (value1 !== value2) {
          // Compare primitive values
          differences.push({
            key: currentPath,
            reason: `Values differ: ${value1} vs ${value2}`,
          });
        }
      }
    }
  }

  compare(obj1, obj2);

  return {
    flag: differences.length === 0,
    differences,
  };
}

export const ticketMapper = (ticket: Ticket) => ({
  "Ticket No": ticket.ticket_number,
  "Customer ID": ticket.customer.id === "0" ? "" : ticket.customer.id,
  "Customer User Name":
    ticket.customer.id === "0" ? "" : ticket.customer.username,
  "Customer Name": ticket.customer.id === "0" ? "" : ticket.customer.name,
  "Customer Number":
    ticket.customer.id === "0" ? "" : ticket.customer.phone_number,
  "Customer Email": ticket.customer.id === "0" ? "" : ticket.customer.email,
  Department: ticket.service.department_name,
  Service: ticket.service.name,
  Counter: `Counter ${ticket.counter_id}`,
  "Wait Time": `${ticket.wait_time} Minutes`,
  Status: ticket.status,
  "Service Agent": ticket.serving_agent.name,
  "Ticket Date": displayDate(ticket.created_at),
  "Last Updatd": displayDate(ticket.updated_at),
});
export const displayDateTimeConversion = (utcDate: string) => {
  const date = new Date(utcDate); // Create a Date object from the UTC string
  return date.toLocaleString("en-PK", {
    timeZone: "Asia/Karachi", // Pakistan Standard Time zone
    hour12: true, // Optional: Use 12-hour clock
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
};

export function maskEmailInText(text: string): string {
  return text.replace(
    /([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/,
    (match, localPart, domain) => {
      if (localPart.length <= 4) return `${localPart[0]}...@${domain}`;
      return `${localPart.slice(0, 4)}...${localPart[localPart.length - 1]}@${domain}`;
    }
  );
}

export function getDomainPart(): string | null {
  try {
    const domain = window.location.host; // e.g., "atmosphere.fitnfi.com" or "localhost:3000"

    // Check if on localhost
    if (domain.startsWith("localhost")) {
      return import.meta.env.VITE_DEFAULT_DOMAIN; // Use a predefined environment variable
    }

    // Regular expression to extract domain part
    const regex = /(?:https?:\/\/)?(?:www\.)?([^/]+)/;
    const match = regex.exec(domain);

    return match ? match[1] : null;
  } catch (error) {
    console.error("Error extracting domain:", error);
    return null; // Fallback value in case of unexpected issues
  }
}


export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.length < 5) return ""; // Handle empty and short inputs

  // If the number starts with '92' but isn't prefixed with '+', assume it's from Pakistan
  let parsedNumber = parsePhoneNumberFromString(
    phoneNumber.startsWith("92") ? `+${phoneNumber}` : phoneNumber
  );

  return parsedNumber ? parsedNumber.formatInternational() : "";
};
export const statusLabels: Record<TicketStatus, string> = {
  [TicketStatus.NEW]: "Pending",
  [TicketStatus.ARRIVED]: "Pending",
  [TicketStatus.CHECKED_IN]: "Check In",
  [TicketStatus.NO_SHOW]: "No Show",
  [TicketStatus.IN_PROGRESS]: "In Progress",
  [TicketStatus.AWAITING_INFO]: "Awaiting information",
  [TicketStatus.HOLD]: "Hold",
  [TicketStatus.CANCELLED]: "Cancelled",
  [TicketStatus.COMPLETED]: "Completed",
  [TicketStatus.COMPLETE]: "Complete",
  [TicketStatus.ARCHIVED]: "Archived",
  [TicketStatus.RESERVED]: "Reserved",
};

export function getStatusLabel(status: TicketStatus): string {
  return statusLabels[status] || "Unknown Status";
}
export const getUserStatusColor = (status: TicketStatus) => {
  switch (status) {
    case TicketStatus.NEW:
      return "bg-[#1976D2] hover:bg-blue-600"; // Dark Blue
    case TicketStatus.ARRIVED:
      return "bg-[#1976D2] hover:bg-blue-600"; // Dark Blue
    case TicketStatus.CHECKED_IN:
      return "bg-[#FBC02D] hover:bg-yellow-600"; // Deep Yellow
    case TicketStatus.NO_SHOW:
      return "bg-[#D32F2F] hover:bg-red-700"; // Dark Red
    case TicketStatus.IN_PROGRESS:
      return "bg-[#F57C00] hover:bg-orange-600"; // Deep Orange
    case TicketStatus.AWAITING_INFO:
      return "bg-[#5D4037] hover:bg-brown-700"; // Dark Brown
    case TicketStatus.HOLD:
      return "bg-[#455A64] hover:bg-gray-700"; // Steel Gray
    case TicketStatus.CANCELLED:
      return "bg-[#616161] hover:bg-gray-600"; // Neutral Gray
    case TicketStatus.COMPLETED:
      return "bg-[#2E7D32] hover:bg-green-700"; // Darker Green
    case TicketStatus.ARCHIVED:
      return "bg-[#37474F] hover:bg-gray-800"; // Dark Gray
    case TicketStatus.RESERVED:
      return "bg-[#00838F] hover:bg-cyan-700"; // Teal
    default:
      return "bg-gray-500 hover:bg-gray-600"; // Default Gray
  }
};

export const statusColors: Record<TicketStatus, string> = {
  [TicketStatus.NEW]: "bg-[#11BBF2]", // Pending
  [TicketStatus.ARRIVED]: "bg-[#11BBF2]", // Pending
  [TicketStatus.CHECKED_IN]: "bg-[#8E221F]", // Serving (assuming)
  [TicketStatus.NO_SHOW]: "bg-[#F6AE05]", // Absent
  [TicketStatus.IN_PROGRESS]: "bg-[#8E221F]", // Serving (assuming)
  [TicketStatus.AWAITING_INFO]: "bg-[#8A93A0]", // Hold
  [TicketStatus.HOLD]: "bg-[#8A93A0]", // Hold
  [TicketStatus.CANCELLED]: "bg-[#F4511E]", // Cancelled
  [TicketStatus.COMPLETED]: "bg-[#28B446]", // Served
  [TicketStatus.COMPLETE]: "bg-[#28B446]", // Served
  [TicketStatus.ARCHIVED]: "bg-[#8A93A0]", // Hold (assuming)
  [TicketStatus.RESERVED]: "bg-[#11BBF2]", // Reserved (assuming)
};

export const getStatusColor = (status: TicketStatus) => {
  switch (status) {
    case TicketStatus.NEW:
      return "bg-[#1976D2] hover:bg-blue-600"; // Dark Blue
    case TicketStatus.ARRIVED:
      return "bg-[#388E3C] hover:bg-green-600"; // Dark Green
    case TicketStatus.CHECKED_IN:
      return "bg-[#FBC02D] hover:bg-yellow-600"; // Deep Yellow
    case TicketStatus.NO_SHOW:
      return "bg-[#D32F2F] hover:bg-red-700"; // Dark Red
    case TicketStatus.IN_PROGRESS:
      return "bg-[#F57C00] hover:bg-orange-600"; // Deep Orange
    case TicketStatus.AWAITING_INFO:
      return "bg-[#5D4037] hover:bg-brown-700"; // Dark Brown
    case TicketStatus.HOLD:
      return "bg-[#455A64] hover:bg-gray-700"; // Steel Gray
    case TicketStatus.CANCELLED:
      return "bg-[#616161] hover:bg-gray-600"; // Neutral Gray
    case TicketStatus.COMPLETED:
      return "bg-[#2E7D32] hover:bg-green-700"; // Darker Green
    case TicketStatus.ARCHIVED:
      return "bg-[#37474F] hover:bg-gray-800"; // Dark Gray
    case TicketStatus.RESERVED:
      return "bg-[#00838F] hover:bg-cyan-700"; // Teal
    default:
      return "bg-gray-500 hover:bg-gray-600"; // Default Gray
  }
};

export const formatTime = (time: string | undefined | null): string => {
  if (!time) return ""; // Return empty string if time is undefined or null

  const parts = time.split(":");
  if (parts.length < 2) return time; // Return original if not in expected format

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Check if conversion resulted in valid numbers
  if (isNaN(hours) || isNaN(minutes)) return time;

  const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const period = hours >= 12 ? "PM" : "AM";

  return `${formattedHours}:${formattedMinutes} ${period}`;
};

export function calculateWaitingTime(
  date: string,
  time: string,
  servedAt: string
): number {
  // Combine date and time to form the full appointment date-time (local time)
  const appointmentDateTime = new Date(`${date}T${time}`);

  // Parse the served_at timestamp (with 'Z', assuming it's in UTC)
  const servedDate = new Date(servedAt + "Z"); // Ensure it's interpreted as UTC

  // Adjust servedAt by adding 5 hours (UTC+5) to it
  const utcPlus5Offset = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  const servedDateUTC5 = new Date(servedDate.getTime());

  // Calculate the difference in milliseconds
  const timeDifference =
    servedDateUTC5.getTime() - appointmentDateTime.getTime();

  // Convert the difference to minutes and return it
  const minutesDifference = Math.max(
    0,
    Math.floor(timeDifference / (1000 * 60))
  );

  return minutesDifference;
}

export function createTagTreeFromTagNodes(tags: TagListItem[]): TagTree[] {
  const map = new Map<string, TagTree>();
  const roots: TagTree[] = [];

  for (const tag of tags) {
    map.set(String(tag.tag_id), { ...tag, children: [] } as TagTree);
  }

  for (const tag of tags) {
    const node = map.get(String(tag.tag_id))!;

    if (tag.path.length === 1) {
      roots.push(node);
    } else if (tag.path.length > 1) {
      const parentId = tag.path[tag.path.length - 2];
      const parent = map.get(String(parentId));
      if (parent) parent.children.push(node);
    }
  }

  return roots;
}

export function getTagPath(tag: TagListItem, byId: Map<number, TagListItem>): string {
  const chain: string[] = [];

  for (const id of tag.path) {
    const node = byId.get(id);
    if (node) {
      chain.push(node.name);
    }
  }

  return chain.join(" > ");
}

